import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

// `live_registrations` (migration 0175) est postérieure aux types générés stale
// (cf. CLAUDE.md) → client générique, comme @/lib/parent-link / leaderboard-tabs.
const db = supabase as unknown as SupabaseClient;

function key(userId: string) {
  return ['live-registrations', userId] as const;
}

/**
 * Ensemble des `challenge_id` auxquels l'élève est inscrit (« Je participe »).
 * Une seule requête légère (toutes mes inscriptions) → l'UI dérive l'état de
 * chaque carte. Désactivée tant qu'aucun utilisateur.
 */
export function useMyLiveRegistrations(userId: string) {
  return useQuery({
    queryKey: key(userId),
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await db
        .from('live_registrations')
        .select('challenge_id')
        .eq('student_id', userId);
      if (error) throw error;
      return new Set(((data ?? []) as Array<{ challenge_id: string }>).map((r) => r.challenge_id));
    },
  });
}

/**
 * Mutations d'inscription : un défi (register/unregister) ou tous les défis à
 * venir de la campagne (registerMany/unregisterMany). Écriture directe PostgREST
 * (RLS own_insert/own_delete). Chaque succès invalide l'ensemble des inscriptions.
 */
export function useLiveRegistrationMutations(userId: string) {
  const qc = useQueryClient();
  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: key(userId) });
    void qc.invalidateQueries({ queryKey: ['live-registration-counts'] });
  }, [qc, userId]);

  const register = useMutation({
    mutationFn: async (challengeIds: string[]) => {
      if (challengeIds.length === 0) return;
      const rows = challengeIds.map((id) => ({ challenge_id: id, student_id: userId }));
      // Idempotent : ignore les inscriptions déjà présentes.
      const { error } = await db
        .from('live_registrations')
        .upsert(rows, { onConflict: 'challenge_id,student_id', ignoreDuplicates: true });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const unregister = useMutation({
    mutationFn: async (challengeIds: string[]) => {
      if (challengeIds.length === 0) return;
      const { error } = await db
        .from('live_registrations')
        .delete()
        .eq('student_id', userId)
        .in('challenge_id', challengeIds);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    register: (id: string) => register.mutate([id]),
    unregister: (id: string) => unregister.mutate([id]),
    registerMany: (ids: string[]) => register.mutate(ids),
    unregisterMany: (ids: string[]) => unregister.mutate(ids),
    isMutating: register.isPending || unregister.isPending,
  };
}

/**
 * Nombre d'inscrits (« Je participe ») de chaque live public demandé (RPC `get_live_registration_counts`,
 * migration 0733) : un nombre seulement, jamais qui. Map vide tant que la RPC ne répond pas (avant la migration).
 */
export function useLiveRegistrationCounts(challengeIds: string[]) {
  const ids = [...new Set(challengeIds)].sort();
  return useQuery({
    queryKey: ['live-registration-counts', ids.join(',')],
    enabled: ids.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<Map<string, number>> => {
      const { data, error } = await db.rpc('get_live_registration_counts', {
        p_challenge_ids: ids.slice(0, 100),
      });
      if (error) return new Map();
      return new Map(Object.entries((data ?? {}) as Record<string, number>));
    },
  });
}

/** « 1 inscrit », « 12 inscrits ». PUR. */
export function registeredLabel(n: number): string {
  return `${n} inscrit${n > 1 ? 's' : ''}`;
}
